// M08 Bildschirm hinzufügen (Assistent, 3 Schritte): Code → Name & Ort (ein Claim-Aufruf) → Inhalt.
import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ApiError, api } from '../api/client';
import { qk, useAssignments, useDevice, useGroups, useLocations, useMe, usePlaylists, useSchedules } from '../api/queries';
import type { Device, Location } from '../api/types';
import { Icon } from '../components/Icon';
import { errorText, useToast } from '../components/toast';
import { Field, PageHead, StatusPill } from '../components/ui';
import { deviceState, formatCode, normalizeCode } from '../lib';

export function ScreenAddPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const { data: me } = useMe();
  const { data: locations = [] } = useLocations();
  const { data: groups = [] } = useGroups();
  const { data: playlists = [] } = usePlaylists();
  const { data: schedules = [] } = useSchedules();
  const { data: assignments = [] } = useAssignments();
  const [step, setStep] = useState(1);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [locationId, setLocationId] = useState('');
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [newLoc, setNewLoc] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [device, setDevice] = useState<Device | null>(null);
  const [content, setContent] = useState<string>('later');
  const [done, setDone] = useState(false);

  const loc = locationId || locations[0]?.id || '';

  const claim = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      let locId = loc;
      if (newLoc !== null && newLoc.trim()) {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Zurich';
        const l = await api.post<Location>('/locations', { name: newLoc.trim(), timezone: tz });
        await qc.invalidateQueries({ queryKey: qk.locations });
        locId = l.id;
        setLocationId(l.id);
        setNewLoc(null);
      }
      const d = await api.post<Device>('/devices/claim', { pairingCode: normalizeCode(code), name: name.trim(), locationId: locId, ...(groupIds.length ? { groupIds } : {}) });
      setDevice(d);
      await qc.invalidateQueries({ queryKey: qk.devices });
      setStep(3);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'pairing_code_invalid') {
        setError(t('screenAdd.codeInvalid'));
        setStep(1);
      } else setError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  const finish = async () => {
    if (!device) return;
    try {
      if (content !== 'later') {
        const [kind, id] = content.split(':');
        await api.put('/assignments', { targetType: 'device', targetId: device.id, ...(kind === 'p' ? { playlistId: id } : { scheduleId: id }) });
        await qc.invalidateQueries({ queryKey: qk.assignments });
      }
      setDone(true);
    } catch (e) {
      toast.error(e);
    }
  };

  const viaLocation = assignments.find((a) => a.targetType === 'location' && a.targetId === (device?.locationId ?? loc))
    ?? assignments.find((a) => a.targetType === 'tenant');
  const viaName = viaLocation ? (viaLocation.playlistId ? playlists.find((p) => p.id === viaLocation.playlistId)?.name : schedules.find((s) => s.id === viaLocation.scheduleId)?.name) : null;

  const steps = [t('screenAdd.step1'), t('screenAdd.step2'), t('screenAdd.step3')];
  if (!me?.permissions.includes('devices.pair')) return <div className="card muted">{t('errors.forbidden')}</div>;

  return (
    <>
      <PageHead title={t('screens.add')} actions={<Link className="btn ghost" to="/screens"><Icon name="left" />{t('common.back')}</Link>} />
      <div className="card" style={{ maxWidth: 720 }}>
        <div className="steps" aria-label={t('screenAdd.progress')}>
          {steps.map((s, i) => (
            <span key={s} className={step === i + 1 ? 'on' : step > i + 1 ? 'done' : ''} aria-current={step === i + 1 ? 'step' : undefined}>
              <b>{step > i + 1 ? '✓' : i + 1}</b>{s}
            </span>
          ))}
        </div>

        {step === 1 && (
          <form className="col" onSubmit={(e) => { e.preventDefault(); if (normalizeCode(code).length === 8) setStep(2); }}>
            <div>{t('screenAdd.codeText')}</div>
            <div className="code-box">
              <input aria-label={t('screenAdd.code')} autoFocus autoComplete="off" spellCheck={false} inputMode="text" placeholder="XXXX-XXXX"
                value={formatCode(code)} onChange={(e) => setCode(normalizeCode(e.target.value))} />
            </div>
            <div className="error" role="alert">{error}</div>
            <div className="hint">{t('screenAdd.noPlayer')} <a href="/play" target="_blank" rel="noreferrer" data-testid="play-link">{`${window.location.origin}/play`}</a> · Android · Windows · Linux</div>
            <div className="row"><span className="grow" /><button className="btn primary" disabled={normalizeCode(code).length !== 8}>{t('common.next')}</button></div>
          </form>
        )}

        {step === 2 && (
          <form className="col" onSubmit={claim}>
            <Field label={t('screenAdd.name')} hint={t('screenAdd.nameHint')}>
              <input required autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={t('screenAdd.namePh')} maxLength={200} />
            </Field>
            {newLoc === null ? (
              <Field label={t('screenAdd.location')}>
                <div className="row">
                  <select className="grow" value={loc} onChange={(e) => setLocationId(e.target.value)}>
                    {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                  {me.permissions.includes('locations.manage') && <button type="button" className="btn" onClick={() => setNewLoc('')}><Icon name="plus" />{t('screenAdd.newLocation')}</button>}
                </div>
              </Field>
            ) : (
              <Field label={t('screenAdd.newLocation')}>
                <div className="row">
                  <input className="grow" autoFocus value={newLoc} onChange={(e) => setNewLoc(e.target.value)} placeholder={t('locations.namePh')} />
                  <button type="button" className="btn ghost" onClick={() => setNewLoc(null)}>{t('common.cancel')}</button>
                </div>
              </Field>
            )}
            {groups.length > 0 && (
              <div className="field">
                {t('screenAdd.groups')}
                <div className="row">
                  {groups.map((g) => (
                    <label key={g.id} className="check"><input type="checkbox" checked={groupIds.includes(g.id)}
                      onChange={(e) => setGroupIds(e.target.checked ? [...groupIds, g.id] : groupIds.filter((x) => x !== g.id))} />{g.name}</label>
                  ))}
                </div>
              </div>
            )}
            <div className="error" role="alert">{error}</div>
            <div className="row">
              <button type="button" className="btn ghost" onClick={() => setStep(1)}>{t('common.back')}</button>
              <span className="grow" />
              <button className="btn primary" disabled={busy || !name.trim() || (!loc && !newLoc)}>{busy ? t('common.loading') : t('screenAdd.pair')}</button>
            </div>
          </form>
        )}

        {step === 3 && device && !done && (
          <div className="col">
            <div className="hint" style={{ background: 'var(--ok-soft)', color: 'var(--ok)', border: 0 }}>{t('screenAdd.paired', { name: device.name })}</div>
            <b>{t('screenAdd.whatRuns')}</b>
            {viaName && <div className="hint">{t('screenAdd.inherited', { name: viaName })}</div>}
            <div className="col" style={{ gap: 6 }} role="radiogroup">
              <label className="check"><input type="radio" name="c" checked={content === 'later'} onChange={() => setContent('later')} />{t('screenAdd.later')}</label>
              {playlists.map((p) => (
                <label key={p.id} className="check"><input type="radio" name="c" checked={content === `p:${p.id}`} onChange={() => setContent(`p:${p.id}`)} />
                  {t('screenAdd.playlist')}: {p.name}</label>
              ))}
              {schedules.map((s) => (
                <label key={s.id} className="check"><input type="radio" name="c" checked={content === `s:${s.id}`} onChange={() => setContent(`s:${s.id}`)} />
                  {t('screenAdd.schedule')}: {s.name}</label>
              ))}
            </div>
            <div className="row"><span className="grow" /><button className="btn primary" onClick={finish}>{t('screenAdd.finish')}</button></div>
          </div>
        )}
        {done && device && <Finished device={device} />}
      </div>
    </>
  );
}

function Finished({ device }: { device: Device }) {
  const { t } = useTranslation();
  const { data } = useDevice(device.id);
  const d = data ?? device;
  const progress = data?.state.syncProgress;
  return (
    <div className="empty">
      <div className="ic"><Icon name="check" /></div>
      <b>{t('screenAdd.doneTitle', { name: d.name })}</b>
      <div className="row" style={{ justifyContent: 'center' }}>
        <StatusPill state={deviceState(d)} />
        {progress !== null && progress !== undefined && progress < 100 && <span className="pill syncing">{t('screenAdd.syncing', { pct: progress })}</span>}
      </div>
      <div className="row" style={{ justifyContent: 'center' }}>
        <Link className="btn" to="/screens/add" reloadDocument>{t('screenAdd.another')}</Link>
        <Link className="btn primary" to={`/screens/${d.id}`}>{t('screenAdd.open')}</Link>
      </div>
    </div>
  );
}
